import React, { useState, useEffect } from 'react';
import { ScanLine, CheckCircle2, AlertTriangle, Mail, Gauge, TrendingUp, Upload, Shield, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import toast from 'react-hot-toast';
import { decodeMimeWords } from '../utils/mime';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [dashboardStats, setDashboardStats] = useState(null);
  const [recentScans, setRecentScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scanFilter, setScanFilter] = useState('');
  const [scanUsers, setScanUsers] = useState([]);
  const [monthlyStats, setMonthlyStats] = useState(null);
  const [dailyData, setDailyData] = useState([]);
  const [quotas, setQuotas] = useState(null);
  const isAdmin = user?.role === 'admin' || user?.role === 'collaborator';

  useEffect(() => { fetchDashboardData(); }, []);
  useEffect(() => { if (isAdmin) fetchMonthlyStats(); }, [isAdmin]);

  const fetchDashboardData = async (userFilter) => {
    try {
      setLoading(true); setError(null);
      const histParams = userFilter ? `limit=5&userId=${userFilter}` : 'limit=5';
      const statsParams = userFilter && userFilter !== 'me' ? `userId=${userFilter}` : '';
      const [statsRes, histRes] = await Promise.allSettled([
        axios.get('/scan/stats' + (statsParams ? '?' + statsParams : '')),
        axios.get('/scan/history?' + histParams)
      ]);
      if (statsRes.status === 'fulfilled') {
        const s = statsRes.value.data.stats;
        setDashboardStats({ totalScans: s.scanned || 0, spamDetected: s.spam || 0, cleanEmails: (s.scanned || 0) - (s.spam || 0), avgScanTime: s.scan_time_avg || 0, emailScans: s.email_scans || 0, manualScans: s.manual_scans || 0 });
      }
      if (histRes.status === 'fulfilled') setRecentScans(histRes.value.data.scans || []);
      if (isAdmin && scanUsers.length === 0) {
        try { const u = await axios.get('/scan/history/users'); setScanUsers(u.data.users || []); } catch {}
      }
    } catch (e) {
      setError('Failed to load dashboard data'); toast.error('Failed to load dashboard data');
    } finally { setLoading(false); }
  };

  const fetchMonthlyStats = async () => {
    try {
      const res = await axios.get('/scan/stats/monthly?days=30');
      setMonthlyStats(res.data.monthly);
      setQuotas(res.data.quotas);
      setDailyData((res.data.daily || []).map(d => ({
        date: d.date.slice(5),
        spam: d.spam,
        clean: d.clean,
      })));
    } catch {}
  };

  const getScoreColor = (score) => score < 5 ? 'text-green-600' : score < 10 ? 'text-yellow-600' : 'text-red-600';
  const getScoreBarColor = (score) => score < 5 ? 'bg-green-500' : score < 10 ? 'bg-yellow-500' : 'bg-red-500';
  const getActionBadge = (action, isSpam) => {
    if (isSpam) return 'bg-red-100 text-red-700';
    if (action === 'no action') return 'bg-green-100 text-green-700';
    return 'bg-yellow-100 text-yellow-700';
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[400px] gap-3">
      <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      <span className="text-gray-500">Loading dashboard...</span>
    </div>
  );

  if (error) return (
    <div>
      <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded mb-4">{error}</div>
      <button onClick={fetchDashboardData} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">Retry</button>
    </div>
  );

  const stats = [
    { icon: ScanLine, color: 'text-blue-600', label: 'Total Scans', value: dashboardStats?.totalScans?.toLocaleString() || '0', sub: 'All time scans' },
    { icon: CheckCircle2, color: 'text-green-600', label: 'Clean Emails', value: dashboardStats?.cleanEmails?.toLocaleString() || '0', sub: dashboardStats?.totalScans > 0 ? `${((dashboardStats.cleanEmails / dashboardStats.totalScans) * 100).toFixed(1)}% of total` : '0% of total' },
    { icon: AlertTriangle, color: 'text-red-600', label: 'Spam Detected', value: dashboardStats?.spamDetected || '0', sub: dashboardStats?.totalScans > 0 ? `${((dashboardStats.spamDetected / dashboardStats.totalScans) * 100).toFixed(1)}% spam rate` : '0% spam rate' },
    { icon: Mail, color: 'text-pink-600', label: 'Scan Emails', value: '1', sub: 'Automatic OAuth mapping' },
    { icon: Gauge, color: 'text-cyan-600', label: 'Avg Scan Time', value: `${dashboardStats?.avgScanTime?.toFixed(2) || '0.0'}s`, sub: 'Average processing time' },
  ];

  return (
    <>
      <Helmet><title>Dashboard - Rspamd Email Scanner</title></Helmet>
      <div>
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold mb-1">Welcome back, {user?.name && !/^\d+$/.test(user.name) ? user.name : user?.email || 'User'}!</h2>
            <p className="text-gray-500">Monitor your email scanning activity and analyze spam detection results.</p>
          </div>
          {isAdmin && scanUsers.length > 0 && (
            <select
              value={scanFilter}
              onChange={e => { setScanFilter(e.target.value); fetchDashboardData(e.target.value); }}
              className="border rounded-lg px-3 py-2 text-sm min-w-[180px]"
            >
              <option value="">All users</option>
              <option value="me">My scans</option>
              {scanUsers.map(u => (
                <option key={u.user_id} value={u.user_id}>{u.email || u.name} ({u.scan_count})</option>
              ))}
            </select>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          {stats.map(s => (
            <div key={s.label} className="bg-white rounded-xl shadow-sm border p-5">
              <div className="flex items-center gap-2 mb-3">
                <s.icon className={`w-5 h-5 ${s.color}`} />
                <span className="text-sm font-medium text-gray-700">{s.label}</span>
              </div>
              <p className="text-3xl font-bold mb-1">{s.value}</p>
              <p className="text-xs text-gray-500">{s.sub}</p>
            </div>
          ))}
        </div>

        {isAdmin && monthlyStats && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-white rounded-xl shadow-sm border p-5">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-blue-600" />
                  <span className="text-sm font-medium text-gray-700">Messages Scanned / Month</span>
                </div>
                <span className="text-lg font-bold">
                  {monthlyStats.scans_this_month?.toLocaleString()}
                  <span className="text-sm font-normal text-gray-500">
                    {' / '}{quotas?.max_emails_per_month ? quotas.max_emails_per_month.toLocaleString() : 'unlimited'}
                  </span>
                </span>
              </div>
              <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                {quotas?.max_emails_per_month ? (
                  <div
                    className={`h-full rounded-full transition-all ${
                      monthlyStats.scans_this_month / quotas.max_emails_per_month > 0.9 ? 'bg-red-500' :
                      monthlyStats.scans_this_month / quotas.max_emails_per_month > 0.7 ? 'bg-yellow-500' : 'bg-blue-500'
                    }`}
                    style={{ width: `${Math.min((monthlyStats.scans_this_month / quotas.max_emails_per_month) * 100, 100)}%` }}
                  />
                ) : (
                  <div className="h-full rounded-full bg-blue-500" style={{ width: '100%', opacity: 0.3 }} />
                )}
              </div>
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>{monthlyStats.spam_this_month} spam / {monthlyStats.clean_this_month} clean</span>
                {quotas?.max_emails_per_month > 0 && (
                  <span>{Math.round((monthlyStats.scans_this_month / quotas.max_emails_per_month) * 100)}% used</span>
                )}
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border p-5">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-purple-600" />
                  <span className="text-sm font-medium text-gray-700">Registered Users</span>
                </div>
                <span className="text-lg font-bold">
                  {monthlyStats.total_users?.toLocaleString()}
                  <span className="text-sm font-normal text-gray-500">
                    {' / '}{quotas?.max_users ? quotas.max_users.toLocaleString() : 'unlimited'}
                  </span>
                </span>
              </div>
              <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                {quotas?.max_users ? (
                  <div
                    className={`h-full rounded-full transition-all ${
                      monthlyStats.total_users / quotas.max_users > 0.9 ? 'bg-red-500' :
                      monthlyStats.total_users / quotas.max_users > 0.7 ? 'bg-yellow-500' : 'bg-purple-500'
                    }`}
                    style={{ width: `${Math.min((monthlyStats.total_users / quotas.max_users) * 100, 100)}%` }}
                  />
                ) : (
                  <div className="h-full rounded-full bg-purple-500" style={{ width: '100%', opacity: 0.3 }} />
                )}
              </div>
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>{monthlyStats.users_this_month} active this month</span>
                {quotas?.max_users > 0 && (
                  <span>{Math.round((monthlyStats.total_users / quotas.max_users) * 100)}% used</span>
                )}
              </div>
            </div>
          </div>
        )}

        {dailyData.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
            <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5" /> Scan Activity (Last 30 Days)
            </h3>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={dailyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="clean" name="Clean" stackId="a" fill="#22c55e" radius={[0, 0, 0, 0]} />
                <Bar dataKey="spam" name="Spam" stackId="a" fill="#ef4444" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h3 className="text-lg font-semibold flex items-center gap-2 mb-4"><TrendingUp className="w-5 h-5" /> Quick Actions</h3>
            <div className="flex flex-col gap-3">
              <button onClick={() => navigate('/scanner')} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition font-medium">
                <ScanLine className="w-5 h-5" /> Scan New Email
              </button>
              <button onClick={() => navigate('/settings')} className="flex items-center gap-2 border border-gray-300 px-4 py-3 rounded-lg hover:bg-gray-50 transition font-medium">
                <Mail className="w-5 h-5" /> Manage Scan Emails
              </button>
              <button onClick={() => navigate('/scanner')} className="flex items-center gap-2 border border-gray-300 px-4 py-3 rounded-lg hover:bg-gray-50 transition font-medium">
                <Upload className="w-5 h-5" /> Upload Email File
              </button>
            </div>
          </div>

          <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border p-6">
            <h3 className="text-lg font-semibold flex items-center gap-2 mb-4"><Shield className="w-5 h-5" /> Recent Scans</h3>
            {recentScans.length > 0 ? (
              <div className="space-y-3">
                {recentScans.map((scan, idx) => (
                  <div key={scan.id || idx} onClick={() => navigate(`/scan-result/${scan.id}`)} className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition">
                    <div className="flex justify-between items-start mb-2">
                      <span className="font-semibold truncate flex-1">{decodeMimeWords(scan.subject) || `Scan #${idx + 1}`}</span>
                      <span className={`ml-2 px-2.5 py-0.5 rounded-full text-xs font-medium ${getActionBadge(scan.action, scan.is_spam)}`}>
                        {scan.is_spam ? 'SPAM' : scan.action === 'no action' ? 'CLEAN' : scan.action?.toUpperCase()}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mb-1">
                      <span className={`text-sm ${getScoreColor(scan.score)}`}>Score: {scan.score?.toFixed(2) || '0.00'}</span>
                      <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${getScoreBarColor(scan.score)}`} style={{ width: `${Math.min((scan.score / 15) * 100, 100)}%` }} />
                      </div>
                      {scan.scan_time && <span className="text-xs text-gray-400">{scan.scan_time}ms</span>}
                    </div>
                    <span className="text-xs text-gray-400">{new Date(scan.created_at || scan.scan_date).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Shield className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500">No scans yet</p>
                <p className="text-sm text-gray-400">Start by scanning your first email</p>
              </div>
            )}
            <div className="text-center mt-4">
              <button onClick={() => navigate('/scanner')} className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                {recentScans.length > 0 ? 'View Scanner' : 'Start Scanning'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Dashboard;
